/**
 * Financial Dashboard Components
 * 
 * Enhanced components for the PSA Financial Management dashboard.
 * These components follow industry standards from Certinia, Kantata, BigTime, etc.
 */

// Core UI Components
export { default as EnhancedKPICard } from './EnhancedKPICard';
export { default as InteractiveChart } from './InteractiveChart';
export { default as ExecutiveSummaryBar } from './ExecutiveSummaryBar';
export { default as FinancialFilters } from './FinancialFilters';
export { default as DrillDownDialog } from './DrillDownDialog';
export { default as FinancialWorkItemsPanel } from './FinancialWorkItemsPanel';
export { default as FinancialEngagementKpiGrid } from './FinancialEngagementKpiGrid';

// Re-export for convenience
export const FINANCIAL_CHART_COLORS = {
    revenue: '#1976D2',
    costs: '#E53935',
    profit: '#43A047',
    forecast: '#7E57C2',
    wip: '#FF9800',
    ar: '#00ACC1',

    // Aging buckets
    ageCurrent: '#4CAF50',
    ageWarning: '#FFC107',
    ageLate: '#FF9800',
    ageCritical: '#F44336',

    // Status
    positive: '#2E7D32',
    negative: '#C62828',
    warning: '#F57C00',
    neutral: '#1565C0'
};

// Format helpers
export const formatters = {
    currency: (value) => {
        if (value === null || value === undefined) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
            maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2
        }).format(value);
    },

    percentage: (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (typeof value === 'string' && value.trim() === '') return 'N/A';
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return 'N/A';
        // Inputs are already canonical percentage magnitudes (for example 84.7, not 0.847).
        // Do not normalize scale here; all scaling happens at backend/model boundaries.
        return `${numericValue.toFixed(1)}%`;
    },

    days: (value) => {
        if (value === null || value === undefined) return 'N/A';
        return `${Math.round(value)} days`;
    },

    number: (value) => {
        if (value === null || value === undefined) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
            maximumFractionDigits: 1
        }).format(value);
    }
};

// Industry benchmark data (SPI Research 2023)
export const INDUSTRY_BENCHMARKS = {
    utilization: {
        bottomQuartile: 0.65,
        median: 0.72,
        topQuartile: 0.78
    },
    profitMargin: {
        bottomQuartile: 0.15,
        median: 0.25,
        topQuartile: 0.35
    },
    dso: {
        bottomQuartile: 60,
        median: 45,
        topQuartile: 35
    },
    realizationRate: {
        bottomQuartile: 0.85,
        median: 0.92,
        topQuartile: 0.97
    },
    revenuePerBillableFTE: {
        bottomQuartile: 180000,
        median: 220000,
        topQuartile: 280000
    }
};

